/* Native PLP — behaviour for sections/main-plp.liquid.
   Filters re-render through the Section Rendering API (?section_id=…); the URL stays in sync, so
   every state is shareable and survives reload/back. One filter panel: "Filter" opens all groups,
   a quick filter word opens only its group right under that word (bottom sheet on phones). */
(() => {
  const desktop = window.matchMedia('(min-width: 992px)');

  const init = (root) => {
    if (root.dataset.plpReady) return;
    root.dataset.plpReady = '1';

    const id = root.dataset.sectionId;
    const form = root.querySelector('[data-plp-form]');
    const panel = root.querySelector('[data-plp-panel]');
    let ctrl = null;
    let timer = null;
    let openBtn = null;

    /* ---------- URL ---------- */
    const buildUrl = () => {
      const p = new URLSearchParams();
      const seen = new Set();
      panel.querySelectorAll('input[data-plp-input]:checked').forEach((i) => {
        const k = `${i.name}\u0000${i.value}`;
        if (seen.has(k)) return;
        seen.add(k);
        p.append(i.name, i.value);
      });
      const price = panel.querySelector('[data-plp-price]');
      if (price) {
        const max = Number(price.dataset.max);
        const nMin = price.querySelector('[data-plp-num="min"]');
        const nMax = price.querySelector('[data-plp-num="max"]');
        const lo = Number(nMin.value) || 0;
        const hi = nMax.value === '' ? max : Number(nMax.value);
        if (lo > 0) p.set(nMin.dataset.name, lo);
        if (hi < max) p.set(nMax.dataset.name, hi);
      }
      const sort = form.querySelector('[data-plp-sort]');
      if (sort && sort.value && sort.value !== sort.dataset.default) p.set('sort_by', sort.value);
      const qs = p.toString();
      return root.dataset.collectionUrl + (qs ? `?${qs}` : '');
    };

    const sectionUrl = (url) => {
      const u = new URL(url, location.origin);
      u.searchParams.set('section_id', id);
      return u.toString();
    };

    /* ---------- Render ---------- */
    const reveal = (items) => {
      if (!('IntersectionObserver' in window)) return;
      const grid = root.querySelector('[data-plp-grid]');
      const cols = (grid && Number(getComputedStyle(grid).getPropertyValue('--cols'))) || 2;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        });
      }, { rootMargin: '0px 0px -4% 0px' });
      items.forEach((el, i) => {
        if (el.classList.contains('plp-reveal')) return;
        el.classList.add('plp-reveal');
        el.style.setProperty('--plp-i', i % cols);
        io.observe(el);
      });
    };

    const swap = (doc, selector) => {
      const next = doc.querySelector(selector);
      const cur = root.querySelector(selector);
      if (next && cur) cur.replaceWith(document.importNode(next, true));
    };

    const stacked = new Set();
    const markStacked = (grp, on) => {
      grp.classList.toggle('is-stacked', on);
      const b = grp.querySelector('[data-plp-stack]');
      if (b) b.setAttribute('aria-expanded', String(on));
    };

    const render = async (url, push = true) => {
      if (ctrl) ctrl.abort();
      ctrl = new AbortController();
      const results = root.querySelector('[data-plp-results]');
      results.classList.add('is-loading');

      const body = panel.querySelector('[data-plp-panel-body]');
      const scroll = body.scrollTop;
      const expanded = [...body.querySelectorAll('.plp-opts.is-expanded')].map((ul) => ul.closest('[data-plp-grp]').dataset.plpGrp);
      // phone rows: keep each row's sideways scroll and its stacked state through the re-render
      const rowScroll = {};
      body.querySelectorAll('[data-plp-grp]').forEach((g) => {
        const row = g.querySelector('.plp-opts--row');
        if (row) rowScroll[g.dataset.plpGrp] = row.scrollLeft;
      });
      const mode = panel.hidden ? null : panel.dataset.mode;

      try {
        const res = await fetch(sectionUrl(url), { signal: ctrl.signal });
        if (!res.ok) throw new Error(res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

        swap(doc, '[data-plp-results]');
        swap(doc, '[data-plp-cats]');
        swap(doc, '.plp-sort__text');
        const q = doc.querySelector('[data-plp-quick]');
        if (q) root.querySelector('[data-plp-quick]').innerHTML = q.innerHTML;
        const d = doc.querySelector('[data-plp-panel-body]');
        if (d) {
          body.innerHTML = d.innerHTML;
          body.scrollTop = scroll;
          expanded.forEach((key) => {
            const grp = body.querySelector(`[data-plp-grp="${CSS.escape(key)}"]`);
            const ul = grp && grp.querySelector('.plp-opts');
            const btn = grp && grp.querySelector('[data-plp-more-opts]');
            if (ul) ul.classList.add('is-expanded');
            if (btn) { btn.dataset.more = btn.textContent; btn.textContent = btn.dataset.less; }
          });
          body.querySelectorAll('[data-plp-grp]').forEach((g) => {
            const key = g.dataset.plpGrp;
            if (stacked.has(key)) markStacked(g, true);
            const row = g.querySelector('.plp-opts--row');
            if (row && rowScroll[key]) row.scrollLeft = rowScroll[key];
          });
        }
        panel.querySelectorAll('[data-plp-price]').forEach(paintPrice);

        const meta = root.querySelector('[data-plp-meta]');
        if (meta) {
          root.querySelectorAll('[data-plp-count]').forEach((el) => { el.textContent = meta.dataset.count; });
          const label = root.querySelector('[data-plp-show-label]');
          if (label) label.textContent = label.dataset.template.replace('[count]', meta.dataset.total);
        }
        if (mode) markMode(mode, root.querySelector(`[data-plp-open="${CSS.escape(mode)}"]`));

        reveal([...root.querySelectorAll('[data-plp-grid] > li')]);
        if (push) history.pushState({ plp: true }, '', url);
      } catch (e) {
        if (e.name === 'AbortError') return;
        location.href = url;
      } finally {
        const r = root.querySelector('[data-plp-results]');
        if (r) r.classList.remove('is-loading');
      }
    };

    const commit = (delay = 250) => {
      clearTimeout(timer);
      timer = setTimeout(() => render(buildUrl()), delay);
    };

    /* ---------- Price ---------- */
    function paintPrice(box) {
      const max = Number(box.dataset.max) || 1;
      const lo = Number(box.querySelector('[data-plp-range="min"]').value);
      const hi = Number(box.querySelector('[data-plp-range="max"]').value);
      const fill = box.querySelector('[data-plp-price-fill]');
      fill.style.setProperty('--a', `${(lo / max) * 100}%`);
      fill.style.setProperty('--b', `${(hi / max) * 100}%`);
      box.querySelectorAll('[data-plp-price-quick]').forEach((b) => {
        b.classList.toggle('is-active', lo === 0 && Number(b.dataset.plpPriceQuick) === hi);
      });
    }

    const setPrice = (box, lo, hi) => {
      box.querySelector('[data-plp-range="min"]').value = lo;
      box.querySelector('[data-plp-range="max"]').value = hi;
      box.querySelector('[data-plp-num="min"]').value = lo;
      box.querySelector('[data-plp-num="max"]').value = hi;
      paintPrice(box);
    };

    /* ---------- Panel ---------- */
    function markMode(mode, btn) {
      panel.dataset.mode = mode;
      panel.querySelectorAll('[data-plp-grp]').forEach((g) => g.classList.toggle('is-current', g.dataset.plpGrp === mode));
      root.querySelectorAll('[data-plp-open]').forEach((b) => b.setAttribute('aria-expanded', String(b === btn)));
      if (btn && mode !== 'all') {
        const bar = form.getBoundingClientRect();
        const r = btn.getBoundingClientRect();
        const x = Math.max(8, Math.min(r.left - bar.left - 24, bar.width - 348));
        panel.style.setProperty('--plp-x', `${x}px`);
      }
    }

    const openPanel = (mode, btn) => {
      markMode(mode, btn);
      openBtn = btn;
      panel.hidden = false;
      if (!desktop.matches) {
        document.documentElement.style.overflow = 'hidden';
        requestAnimationFrame(() => requestAnimationFrame(() => panel.classList.add('is-open')));
      } else {
        panel.classList.add('is-open');
      }
    };

    const closePanel = () => {
      if (panel.hidden) return;
      panel.classList.remove('is-open');
      root.querySelectorAll('[data-plp-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      document.documentElement.style.overflow = '';
      openBtn = null;
      if (desktop.matches) panel.hidden = true;
      else setTimeout(() => { panel.hidden = true; }, 350);
    };

    /* ---------- Events (delegated: re-rendered markup keeps working) ---------- */
    root.addEventListener('click', async (e) => {
      const t = e.target;

      const opener = t.closest('[data-plp-open]');
      if (opener) {
        const mode = opener.dataset.plpOpen;
        if (!panel.hidden && panel.dataset.mode === mode) closePanel();
        else openPanel(mode, opener);
        return;
      }
      if (t.closest('[data-plp-close]')) { closePanel(); return; }

      const density = t.closest('[data-plp-density]');
      if (density) {
        root.dataset.density = density.dataset.plpDensity;
        try { localStorage.setItem('plp-density', root.dataset.density); } catch (err) { /* private mode */ }
        return;
      }

      const slideBtn = t.closest('[data-plp-slide]');
      if (slideBtn) {
        const track = slideBtn.parentElement.querySelector('[data-plp-track]');
        track.scrollBy({ left: Number(slideBtn.dataset.plpSlide) * track.clientWidth, behavior: 'smooth' });
        return;
      }

      const cat = t.closest('[data-plp-cat]');
      if (cat) {
        e.preventDefault();
        const p = new URLSearchParams(location.search);
        p.delete(cat.dataset.plpCat);
        p.delete('page');
        if (cat.dataset.value && !cat.classList.contains('is-active')) p.append(cat.dataset.plpCat, cat.dataset.value);
        const qs = p.toString();
        render(root.dataset.collectionUrl + (qs ? `?${qs}` : ''));
        return;
      }

      const quick = t.closest('[data-plp-price-quick]');
      if (quick) {
        setPrice(quick.closest('[data-plp-price]'), 0, Number(quick.dataset.plpPriceQuick));
        commit(0);
        return;
      }

      const stackBtn = t.closest('[data-plp-stack]');
      if (stackBtn) {
        const grp = stackBtn.closest('[data-plp-grp]');
        const on = !grp.classList.contains('is-stacked');
        markStacked(grp, on);
        if (on) stacked.add(grp.dataset.plpGrp); else stacked.delete(grp.dataset.plpGrp);
        return;
      }

      const sortPick = t.closest('[data-plp-sort-pick]');
      if (sortPick) {
        const sel = form.querySelector('[data-plp-sort]');
        if (sel && sel.value !== sortPick.dataset.plpSortPick) {
          sel.value = sortPick.dataset.plpSortPick;
          sel.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      const more = t.closest('[data-plp-more-opts]');
      if (more) {
        const ul = more.parentElement.querySelector('.plp-opts');
        const on = ul.classList.toggle('is-expanded');
        if (on) { more.dataset.more = more.textContent; more.textContent = more.dataset.less; }
        else more.textContent = more.dataset.more;
        return;
      }

      const link = t.closest('[data-plp-link]');
      if (link) {
        e.preventDefault();
        render(link.href);
        return;
      }

      const next = t.closest('[data-plp-next]');
      if (next) {
        e.preventDefault();
        next.setAttribute('aria-busy', 'true');
        try {
          const res = await fetch(sectionUrl(next.href));
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const grid = root.querySelector('[data-plp-grid]');
          doc.querySelectorAll('[data-plp-grid] > li').forEach((li) => grid.appendChild(document.importNode(li, true)));
          swap(doc, '[data-plp-more]');
          reveal([...grid.children]);
        } catch (err) {
          location.href = next.href;
        }
        return;
      }

      const aboutBtn = t.closest('[data-plp-about-toggle]');
      if (aboutBtn) {
        const text = root.querySelector('[data-plp-about]');
        const clamped = text.classList.toggle('is-clamped');
        if (!clamped) { aboutBtn.dataset.more = aboutBtn.textContent; aboutBtn.textContent = aboutBtn.dataset.less; }
        else aboutBtn.textContent = aboutBtn.dataset.more;
      }
    });

    root.addEventListener('change', (e) => {
      const t = e.target;
      if (t.matches('[data-plp-input]')) commit();
      else if (t.matches('[data-plp-sort]') || t.matches('[data-plp-range]')) commit(0);
      else if (t.matches('[data-plp-num]')) {
        const box = t.closest('[data-plp-price]');
        const max = Number(box.dataset.max);
        let lo = Number(box.querySelector('[data-plp-num="min"]').value) || 0;
        let hi = Number(box.querySelector('[data-plp-num="max"]').value) || max;
        lo = Math.max(0, Math.min(lo, max));
        hi = Math.max(lo, Math.min(hi, max));
        setPrice(box, lo, hi);
        commit(0);
      }
    });

    root.addEventListener('input', (e) => {
      const t = e.target;
      if (t.matches('[data-plp-range]')) {
        const box = t.closest('[data-plp-price]');
        const a = box.querySelector('[data-plp-range="min"]');
        const b = box.querySelector('[data-plp-range="max"]');
        let lo = Number(a.value);
        let hi = Number(b.value);
        if (lo > hi) { if (t === a) lo = hi; else hi = lo; }
        setPrice(box, lo, hi);
      } else if (t.matches('[data-plp-search]')) {
        const term = t.value.trim().toLowerCase();
        const ul = t.parentElement.querySelector('.plp-opts');
        ul.classList.toggle('is-searching', term !== '');
        ul.querySelectorAll('[data-plp-opt]').forEach((li) => { li.hidden = term !== '' && !li.dataset.plpOpt.includes(term); });
      }
    });

    // Card image tracks: scroll doesn't bubble, so listen in the capture phase
    let raf = 0;
    root.addEventListener('scroll', (e) => {
      const track = e.target;
      if (!(track instanceof Element) || !track.matches('[data-plp-track]')) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const media = track.parentElement;
        const i = Math.round(track.scrollLeft / track.clientWidth);
        const last = track.children.length - 1;
        const bar = media.querySelector('[data-plp-progress]');
        if (bar) bar.style.setProperty('--i', i);
        const prev = media.querySelector('.plp-card__nav--prev');
        const next = media.querySelector('.plp-card__nav--next');
        if (prev) prev.hidden = i <= 0;
        if (next) next.hidden = i >= last;
      });
    }, { capture: true, passive: true });

    form.addEventListener('submit', (e) => { e.preventDefault(); render(buildUrl()); });
    document.addEventListener('click', (e) => {
      if (!panel.hidden && desktop.matches && !form.contains(e.target)) closePanel();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || panel.hidden) return;
      const btn = openBtn;
      closePanel();
      if (btn) btn.focus();
    });
    window.addEventListener('popstate', () => render(location.href, false));

    /* ---------- First paint ---------- */
    panel.querySelectorAll('[data-plp-price]').forEach(paintPrice);
    reveal([...root.querySelectorAll('[data-plp-grid] > li')].slice(8));
    const about = root.querySelector('[data-plp-about]');
    const aboutBtn = root.querySelector('[data-plp-about-toggle]');
    if (about && aboutBtn) {
      about.classList.add('is-clamped');
      if (about.scrollHeight > about.clientHeight + 4) aboutBtn.hidden = false;
      else about.classList.remove('is-clamped');
    }
  };

  const boot = () => document.querySelectorAll('[data-plp]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
